import React, { forwardRef, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useDispatch, connect, useSelector } from 'react-redux';
import { IAppState } from 'spw-redux/reducer';
import { ResizableBox } from 'react-resizable';
import { Row, SPWMainButton } from 'spw-shared/globalComponents';
import {
  deleteCategoryRowsFromProject,
  deleteChildWorksheetSpaceRowsFromProject,
  deleteChildSpaceTypeFromProject as deleteChildSpaceTypeFromProjectAction,
  deleteSpaceTypeFromProject as deleteSpaceTypeFromProjectAction,
  deleteSpaceTypeRowsFromProject as deleteSpaceTypeRowsFromProjectAction,
  setRemoveSpaceTypeDialogStatus as setRemoveSpaceTypeDialogStatusAction,
} from 'spw-redux/modules/worksheet/actions';
import { IDepartment, IProject } from 'spw-redux/modules/projects/types';
import {
  CalculatorType,
  IChildWorksheetSpaceType,
} from 'spw-redux/modules/calculators/types';
import {
  DeleteButton,
  RemoveSpaceTypeCustomDialog,
  RemoveSpaceTypeDialogActions,
  RemoveSpaceTypeDialogTitle,
  RemoveSpaceTypeCautionMessage,
  RemoveSpaceTypeDialogBox,
  ScenarioListColumn,
  ScenarioListRow,
  ScenariosContainer,
  ScenariosListContainer,
  ScenariosListHeader,
  ScenariosListHeaderColumn,
  ScenariosListTitle,
  ScenariosSection,
  SpaceTypeText,
  ListItemText,
  WorksheetsNumber,
  DepartmentColumn,
  RemoveSpaceTypeDialogContent,
} from 'spw-components/dialogs/worksheet/styles';
import { GROWTH_CALCULATORS } from 'spw-constants';
import makeStyles from '@mui/styles/makeStyles';
import DragHandle from '@mui/icons-material/DragHandle';
import { groupBy } from 'ramda';
import {
  getScenarioFloors,
  sortFloorScenarios,
  sortScenarios,
} from 'spw-components/programming-scenarios/utils';
import {
  getChildSpaceTypesMap,
  isStorageSupportSpace,
  removeRowFromWorksheet,
} from 'spw-components/worksheet/utils';
import { asUsfOrZero, removeLineBreaks } from 'spw-shared/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { SecondaryIdentifierValue } from 'spw-components/programming-scenarios/styles';
import { sortSpaceTypesAsWorksheetRows } from 'spw-redux/modules/calculators/utils';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { selectProjectSettings } from 'spw-redux/modules/settings/selector';
import {
  selectScenariosUsingSpaceTypeOrCategory,
  selectSelectedChildSpaceType,
  selectSelectedSpaceType,
  selectSelectedSpaceTypeCategory,
  selectWorksheetsUsingSpaceTypeOrCategory,
} from 'spw-redux/modules/worksheet/selectors';
import { populateWorksheet } from 'spw-redux/modules/calculators/actions';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import WorksheetStorageSpaceRow from '../../../models/WorksheetStorageSpaceRow';
import Worksheet, { PlainWorksheet } from '../../../models/Worksheet';
import WorksheetRow from '../../../models/WorksheetRow';
import { PlainProgramScenario } from '../../../models/Scenario';
import { ClientsOrInternalNamesTitle } from '../spaces/styles';

interface IRemoveSpaceType {
  worksheet: PlainWorksheet;
  childWorksheetSpaces: IChildWorksheetSpaceType[];
  showDialog: boolean;
  currentProject: IProject;
  departments: IDepartment[];
  hideAllMode: boolean;
}

const useStyles = makeStyles({
  resizable: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  resizeHandler: {
    width: '100%',
    cursor: 'ns-resize',
  },
});

const ResizeHandle = forwardRef(({ handleAxis, ...otherProps }: any, ref) => {
  const classes = useStyles();

  return (
    <Box
      bgcolor="#E0E0E0"
      display="flex"
      flex={0}
      height={24}
      alignContent="center"
      justifyContent="center"
      ref={ref}
      className={`handle-${handleAxis} ${classes.resizeHandler}`}
      {...otherProps}
    >
      <DragHandle />
    </Box>
  );
});

const BUILDING_TYPE = 'building';
const FLOOR_TYPE = 'floor';

const calculatorTypes = [
  {
    type: GROWTH_CALCULATORS.macroProgramming,
    header: 'Macro Scenarios',
    name: 'Macro',
  },
  {
    type: GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments,
    header: 'Department Scenarios',
    name: 'Department',
  },
  {
    type: BUILDING_TYPE,
    header: 'Building Scenarios',
    name: 'Building',
  },
  {
    type: FLOOR_TYPE,
    header: 'Floor Scenarios',
    name: 'Floor',
  },
];

const wideModalStyles = {
  style: { minWidth: 844 },
};

const RemoveSpaceTypeDialog: React.FunctionComponent<IRemoveSpaceType> = ({
  worksheet,
  childWorksheetSpaces,
  showDialog,
  currentProject,
  hideAllMode,
}) => {
  const dispatch = useDispatch();
  const currentScenario = useSelector(selectCurrentScenario);
  const projectSettings = useSelector(selectProjectSettings);
  const scenariosUsingSpaceTypeOrCategory = useSelector(
    selectScenariosUsingSpaceTypeOrCategory
  );
  const worksheetsByScenarioIdMap = useSelector(
    selectWorksheetsUsingSpaceTypeOrCategory
  );

  const selectedSpaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  );
  const selectedSpaceType = useSelector(selectSelectedSpaceType);
  const selectedChildSpaceType = useSelector(selectSelectedChildSpaceType);

  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );

  const { getUnit, convertArea } = useUnitConversion();

  const floors = currentProject.buildings[0]?.floors
    ? getScenarioFloors(currentProject.buildings[0].floors)
    : {};

  const refMap = calculatorTypes.reduce(
    (
      map: Record<string, HTMLDivElement | null>,
      calculatorType
    ): Record<string, HTMLDivElement | null> => {
      return {
        ...map,
        [calculatorType.type]: null,
      };
    },
    {}
  );

  const [selectedCalculatorType, setSelectedCalculatorType] = useState(
    GROWTH_CALCULATORS.macroProgramming
  );

  const onClose = () => {
    dispatch(setRemoveSpaceTypeDialogStatusAction(false));
  };

  const scenariosByType = groupBy((scenario: PlainProgramScenario) => {
    const { calculatorType } = scenario;
    if (
      calculatorType === GROWTH_CALCULATORS.floorHeadcountSeatcount ||
      calculatorType === GROWTH_CALCULATORS.floorUsf
    ) {
      return FLOOR_TYPE;
    }

    if (
      calculatorType === GROWTH_CALCULATORS.buildingHeadcountSeatcount ||
      calculatorType === GROWTH_CALCULATORS.buildingUsf
    ) {
      return BUILDING_TYPE;
    }

    return calculatorType;
  })(scenariosUsingSpaceTypeOrCategory);

  const isCustomSpaceTypeParent = !selectedChildSpaceType;

  const onDeleteSpaceType = () => {
    if (hideAllMode) {
      if (selectedSpaceTypeCategory) {
        dispatch(deleteCategoryRowsFromProject(selectedSpaceTypeCategory.id));
      } else if (selectedChildSpaceType) {
        dispatch(
          deleteChildWorksheetSpaceRowsFromProject(selectedChildSpaceType.id)
        );
      } else {
        dispatch(deleteSpaceTypeRowsFromProjectAction(selectedSpaceType.id));
      }
    } else if (selectedChildSpaceType) {
      dispatch(
        deleteChildSpaceTypeFromProjectAction(selectedChildSpaceType.id)
      );
    } else {
      dispatch(deleteSpaceTypeFromProjectAction(selectedSpaceType.id));
    }

    if (selectedSpaceType || selectedChildSpaceType) {
      const { rows } = worksheet;
      const rowToDelete = rows.find((row) => {
        if (selectedSpaceType) {
          return row?.projectSpaceType?.id === selectedSpaceType.id;
        }
        return row?.child && row?.spaceId === selectedChildSpaceType.id;
      });

      if (rowToDelete) {
        const newRows = removeRowFromWorksheet(rowToDelete, rows);
        const updatedWorksheet = Worksheet.toAPIWorksheet(
          {
            ...worksheet,
            rows: newRows,
          },
          false
        );
        dispatch(
          populateWorksheet(
            new Worksheet(updatedWorksheet, currentScenario, projectSettings)
          )
        );
      }
    }

    onClose();
  };

  const classes = useStyles();

  const selectedSpaceTypeName = useMemo(() => {
    if (selectedSpaceTypeCategory) {
      return selectedSpaceTypeCategory.name;
    }

    if (selectedSpaceType) {
      return clientFacingNamesAreVisible && selectedSpaceType.clientFacingName
        ? selectedSpaceType?.clientFacingName
        : selectedSpaceType.spaceType.name;
    }

    return clientFacingNamesAreVisible &&
      selectedChildSpaceType.clientFacingName
      ? selectedChildSpaceType.clientFacingName
      : selectedChildSpaceType.name;
  }, [
    clientFacingNamesAreVisible,
    selectedSpaceTypeCategory,
    selectedSpaceType,
    selectedChildSpaceType,
  ]);

  const scrollToList = (calculatorType: string) => {
    if (refMap[calculatorType]) {
      refMap[calculatorType].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  let childSpaceTypes: Record<number, IChildWorksheetSpaceType>;

  if (isCustomSpaceTypeParent) {
    childSpaceTypes = getChildSpaceTypesMap(childWorksheetSpaces);
  }

  const getChildSpaceTypeName = (row: WorksheetRow) => {
    if (
      clientFacingNamesAreVisible &&
      row.child &&
      row.projectSpaceType.clientFacingName
    )
      return row.projectSpaceType.clientFacingName;
    return row.child ? childSpaceTypes[row.spaceId]?.name : row.name;
  };

  const categoryType = useMemo(() => {
    return selectedSpaceTypeCategory?.parentId
      ? 'Subcategory'
      : 'Space Category';
  }, [selectedSpaceTypeCategory]);

  const isStorageSpaceType = useMemo(
    () =>
      isStorageSupportSpace(
        selectedSpaceType?.spaceType || selectedChildSpaceType?.parent
      ),
    [selectedSpaceType, selectedChildSpaceType]
  );

  const isHideAll = hideAllMode || isStorageSpaceType;

  if (!showDialog) return null;

  return (
    <RemoveSpaceTypeCustomDialog
      open={showDialog}
      onClose={onClose}
      PaperProps={isCustomSpaceTypeParent ? wideModalStyles : undefined}
    >
      <ResizableBox
        width={Infinity}
        height={scenariosUsingSpaceTypeOrCategory.length ? 666 : 310}
        minConstraints={[
          Infinity,
          scenariosUsingSpaceTypeOrCategory.length ? 666 : 310,
        ]}
        className={
          scenariosUsingSpaceTypeOrCategory.length > 0
            ? classes.resizable
            : null
        }
        handle={
          scenariosUsingSpaceTypeOrCategory.length > 0 && <ResizeHandle />
        }
      >
        <>
          <Row justifyContent="space-between" alignItems="center">
            <RemoveSpaceTypeDialogTitle>
              {isHideAll
                ? `Hide ${selectedSpaceTypeName} on All Worksheets`
                : 'Delete Custom Space Type'}
            </RemoveSpaceTypeDialogTitle>
            <ClientsOrInternalNamesTitle>
              {clientFacingNamesAreVisible ? 'Client Names' : 'Internal Names'}
            </ClientsOrInternalNamesTitle>
          </Row>
          <RemoveSpaceTypeDialogContent>
            <Box display="flex" flexDirection="column" height="100%">
              <RemoveSpaceTypeCautionMessage>
                <div className="red">CAUTION</div>
                <p>
                  This action will apply to all worksheets in this project and
                  cannot be undone.
                </p>
                <p>
                  All values
                  {isCustomSpaceTypeParent ? ' including nested rows ' : ' '}
                  associated with this
                  {selectedSpaceTypeCategory
                    ? ` ${categoryType.toLowerCase()} `
                    : ' space type '}
                  will {isHideAll ? '' : 'also'} be deleted.
                </p>
              </RemoveSpaceTypeCautionMessage>
              {isCustomSpaceTypeParent &&
              scenariosUsingSpaceTypeOrCategory.length > 0 ? (
                <SpaceTypeText>
                  <span className="bold">
                    <pre>{removeLineBreaks(selectedSpaceTypeName)}</pre>{' '}
                  </span>{' '}
                  is associated with the worksheets listed below.
                </SpaceTypeText>
              ) : (
                <SpaceTypeText>
                  <span className="bold">
                    <pre>{removeLineBreaks(selectedSpaceTypeName)}</pre>
                  </span>{' '}
                  is being used in{' '}
                  <span className="bold">
                    {scenariosUsingSpaceTypeOrCategory.length}
                  </span>{' '}
                  worksheet
                  {scenariosUsingSpaceTypeOrCategory.length === 1 ? '' : 's'}.
                </SpaceTypeText>
              )}

              {scenariosUsingSpaceTypeOrCategory.length > 0 && (
                <>
                  <Grid container style={{ paddingRight: 24 }}>
                    {calculatorTypes.map((calculatorType, index) => {
                      const isSelected =
                        calculatorType.type === selectedCalculatorType;
                      const shouldRemoveMargin = index === 0;
                      return (
                        <Grid
                          item
                          xs={3}
                          key={`calculator-${calculatorType.type}`}
                        >
                          <RemoveSpaceTypeDialogBox
                            className={`${shouldRemoveMargin ? 'ml-0' : ''} ${
                              isSelected ? 'selected' : 'selectable'
                            }`}
                            onClick={() => {
                              setSelectedCalculatorType(
                                calculatorType.type as CalculatorType
                              );
                              scrollToList(calculatorType.type);
                            }}
                          >
                            {calculatorType.name}
                          </RemoveSpaceTypeDialogBox>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <ScenariosSection>
                    <ScenariosContainer>
                      {calculatorTypes.map((calculatorType) => {
                        const detailedScenarios: PlainProgramScenario[] = [];
                        const summaryScenarios: PlainProgramScenario[] = [];

                        if (scenariosByType[calculatorType.type])
                          scenariosByType[calculatorType.type].forEach(
                            (scenario) => {
                              if (scenario.isDetailed)
                                detailedScenarios.push(scenario);
                              else summaryScenarios.push(scenario);
                            }
                          );
                        const sortedDetailedScenarios = (calculatorType.type ===
                        FLOOR_TYPE
                          ? sortFloorScenarios(detailedScenarios, floors)
                          : sortScenarios(
                              detailedScenarios
                            )) as PlainProgramScenario[];
                        const sortedSummaryScenarios = (calculatorType.type ===
                        FLOOR_TYPE
                          ? sortFloorScenarios(summaryScenarios, floors)
                          : sortScenarios(
                              summaryScenarios
                            )) as PlainProgramScenario[];

                        const scenarios: PlainProgramScenario[] = sortedSummaryScenarios.concat(
                          sortedDetailedScenarios
                        );

                        return (
                          <ScenariosListContainer
                            key={`${calculatorType.type}-list`}
                            ref={(node) => {
                              refMap[calculatorType.type] = node;
                            }}
                          >
                            {isCustomSpaceTypeParent ? (
                              <ScenariosListTitle>
                                <Box>
                                  <b>
                                    {scenarios.length} {calculatorType.name}
                                  </b>{' '}
                                  worksheets use space types that will be{' '}
                                  {isHideAll ? 'hidden' : 'deleted'}
                                </Box>
                              </ScenariosListTitle>
                            ) : (
                              <ScenariosListTitle>
                                <b>
                                  <Box>{calculatorType.header}</Box>
                                </b>

                                <WorksheetsNumber
                                  display="flex"
                                  justifyContent="flex-end"
                                  flexGrow="1"
                                >
                                  {scenarios.length} worksheets
                                </WorksheetsNumber>
                              </ScenariosListTitle>
                            )}

                            {scenarios.length > 0 && (
                              <>
                                <ScenariosListHeader container>
                                  {calculatorType.type ===
                                    GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments ||
                                  calculatorType.type === FLOOR_TYPE ? (
                                    <>
                                      <ScenariosListHeaderColumn
                                        item
                                        xs={isCustomSpaceTypeParent ? 3 : 4}
                                      >
                                        SCENARIO NAME
                                      </ScenariosListHeaderColumn>
                                      <ScenariosListHeaderColumn
                                        item
                                        xs={isCustomSpaceTypeParent ? 3 : 4}
                                      >
                                        {calculatorType.type ===
                                        GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments
                                          ? 'DEPARTMENT NAME'
                                          : 'FLOOR NAME'}
                                      </ScenariosListHeaderColumn>
                                    </>
                                  ) : (
                                    <ScenariosListHeaderColumn
                                      item
                                      xs={isCustomSpaceTypeParent ? 6 : 8}
                                    >
                                      SCENARIO NAME
                                    </ScenariosListHeaderColumn>
                                  )}
                                  {isCustomSpaceTypeParent && (
                                    <ScenariosListHeaderColumn item xs={3}>
                                      Space Type Name
                                    </ScenariosListHeaderColumn>
                                  )}
                                  <ScenariosListHeaderColumn
                                    item
                                    xs={isCustomSpaceTypeParent ? 1 : 2}
                                    className="right"
                                  >
                                    QUANTITY
                                  </ScenariosListHeaderColumn>
                                  <ScenariosListHeaderColumn
                                    item
                                    xs={isCustomSpaceTypeParent ? 2 : 2}
                                    className="right"
                                  >
                                    TOTAL {getUnit('area')}
                                  </ScenariosListHeaderColumn>
                                </ScenariosListHeader>

                                {scenarios.map(
                                  (
                                    scenario: PlainProgramScenario,
                                    scenarioIndex: number
                                  ) => {
                                    const {
                                      rows: rowsToBeDeleted,
                                    } = worksheetsByScenarioIdMap.byId[
                                      scenario.worksheet
                                    ];

                                    const isLastScenario =
                                      scenarioIndex === scenarios.length - 1;
                                    // sorting by space type works because we don't support user sorting (drag and drop) of space types within IW
                                    const sortedRows = rowsToBeDeleted
                                      .filter(
                                        (row) =>
                                          !row.removed &&
                                          (isStorageSpaceType
                                            ? (row as WorksheetStorageSpaceRow)
                                                .lockersOrDrawersQuantity
                                            : row.quantity)
                                      )
                                      .sort((a, b) =>
                                        sortSpaceTypesAsWorksheetRows(
                                          a,
                                          b,
                                          worksheet
                                        )
                                      );

                                    return sortedRows.map(
                                      (row, rowIndex: number) => {
                                        const isLastRow =
                                          rowIndex === sortedRows.length - 1;
                                        const shouldHideSeparator =
                                          isLastScenario && isLastRow;

                                        return (
                                          <ScenarioListRow
                                            key={`row-${row.id}`}
                                            className={
                                              shouldHideSeparator
                                                ? 'border-bottom-0'
                                                : ''
                                            }
                                          >
                                            {calculatorType.type ===
                                              GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments ||
                                            calculatorType.type ===
                                              FLOOR_TYPE ? (
                                              <>
                                                <ScenarioListColumn
                                                  item
                                                  xs={
                                                    isCustomSpaceTypeParent
                                                      ? 3
                                                      : 4
                                                  }
                                                >
                                                  <ListItemText
                                                    primary={scenario.name}
                                                  />
                                                </ScenarioListColumn>
                                                {calculatorType.type ===
                                                GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments ? (
                                                  <ScenarioListColumn
                                                    item
                                                    xs={
                                                      isCustomSpaceTypeParent
                                                        ? 3
                                                        : 4
                                                    }
                                                  >
                                                    <DepartmentColumn>
                                                      <ListItemText
                                                        primary={
                                                          scenario.department
                                                            .name
                                                        }
                                                      />
                                                      <SecondaryIdentifierValue>
                                                        {
                                                          scenario.department
                                                            .secondaryIdentifier
                                                        }
                                                      </SecondaryIdentifierValue>
                                                    </DepartmentColumn>
                                                  </ScenarioListColumn>
                                                ) : (
                                                  <ScenarioListColumn
                                                    item
                                                    xs={
                                                      isCustomSpaceTypeParent
                                                        ? 3
                                                        : 4
                                                    }
                                                  >
                                                    <ListItemText
                                                      primary={
                                                        floors[scenario.floorId]
                                                          ?.name
                                                      }
                                                    />
                                                  </ScenarioListColumn>
                                                )}
                                              </>
                                            ) : (
                                              <ScenarioListColumn
                                                item
                                                xs={
                                                  isCustomSpaceTypeParent
                                                    ? 6
                                                    : 8
                                                }
                                              >
                                                <ListItemText
                                                  primary={scenario.name}
                                                />
                                              </ScenarioListColumn>
                                            )}
                                            {isCustomSpaceTypeParent && (
                                              <ScenarioListColumn item xs={3}>
                                                <DepartmentColumn>
                                                  <ListItemText
                                                    primary={
                                                      <pre>
                                                        {removeLineBreaks(
                                                          selectedSpaceTypeName
                                                        )}
                                                      </pre>
                                                    }
                                                  />
                                                  <SecondaryIdentifierValue>
                                                    <pre>
                                                      {removeLineBreaks(
                                                        getChildSpaceTypeName(
                                                          row
                                                        )
                                                      )}
                                                    </pre>
                                                  </SecondaryIdentifierValue>
                                                </DepartmentColumn>
                                              </ScenarioListColumn>
                                            )}
                                            <ScenarioListColumn
                                              item
                                              xs={
                                                isCustomSpaceTypeParent ? 1 : 2
                                              }
                                              className="right"
                                            >
                                              {asUsfOrZero(
                                                isStorageSpaceType
                                                  ? (row as WorksheetStorageSpaceRow)
                                                      .lockersOrDrawersQuantity
                                                  : row.quantity
                                              )}
                                            </ScenarioListColumn>
                                            <ScenarioListColumn
                                              item
                                              xs={2}
                                              className="right"
                                            >
                                              {asUsfOrZero(
                                                convertArea(row.totalUsf)
                                              )}
                                            </ScenarioListColumn>
                                          </ScenarioListRow>
                                        );
                                      }
                                    );
                                  }
                                )}
                              </>
                            )}
                          </ScenariosListContainer>
                        );
                      })}
                    </ScenariosContainer>
                  </ScenariosSection>
                </>
              )}
            </Box>
          </RemoveSpaceTypeDialogContent>
          <RemoveSpaceTypeDialogActions>
            <SPWMainButton onClick={onClose}>No, go back</SPWMainButton>
            <DeleteButton onClick={onDeleteSpaceType}>
              {`Yes, ${isHideAll ? 'hide' : 'delete'} ${
                selectedSpaceTypeCategory ? ` ${categoryType}` : ' Space Type'
              }`}
            </DeleteButton>
          </RemoveSpaceTypeDialogActions>
        </>
      </ResizableBox>
    </RemoveSpaceTypeCustomDialog>
  );
};

const mapStateToProps = (state: IAppState) => ({
  worksheet: state.calculatorReducer.worksheet,
  childWorksheetSpaces: state.calculatorReducer.childWorksheetSpaces,
  showDialog: state.worksheetReducer.showRemoveSpaceTypeDialog,
  currentProject: state.projectsReducer.currentProject,
  departments: state.projectsReducer.departments,
  hideAllMode: state.worksheetReducer.hideAllMode,
});

export default connect(mapStateToProps)(RemoveSpaceTypeDialog);
